import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { Filter } from '@vk-hr-tek/core/filter';
import { Logger } from '@vk-hr-tek/core/logger';
import { t } from '@vk-hr-tek/core/translations';
import { ValidationService } from '@vk-hr-tek/core/validation';

import {
  Event as EventDetailEntity,
  EventListItem as EventListEntity,
  Action,
  Attribute,
  EventEmployee,
} from '@app/gen/events';

import {
  VACATION_START_ATTR_ID,
  VACATION_END_ATTR_ID,
  FIRST_VACATION_START_ATTR_ID,
  SECOND_VACATION_START_ATTR_ID,
  THIRD_VACATION_START_ATTR_ID,
  FOURTH_VACATION_START_ATTR_ID,
  FIFTH_VACATION_START_ATTR_ID,
  FIRST_VACATION_END_ATTR_ID,
  SECOND_VACATION_END_ATTR_ID,
  THIRD_VACATION_END_ATTR_ID,
  FOURTH_VACATION_END_ATTR_ID,
  FIFTH_VACATION_END_ATTR_ID,
  PERIOD_START_ID,
  PERIOD_END_ID,
  DISMISSAL_DATE_ATTR_ID,
  CONTRACT_DATE_ATTR_ID,
  PLACEMENT_END_DATE_ATTR_ID,
  PROBATION_START_DATE_ATTR_ID,
  PROBATION_END_DATE_ATTR_ID,
  STAGE_COLORS,
  BUSINESS_TRIP_PERIODS,
} from '../constants';
import { GetEventsDto } from '../dto';
import type { EventUnitData, AbsencesFilterType } from '../types';

const vacationPeriods = [
  {
    start: VACATION_START_ATTR_ID,
    end: VACATION_END_ATTR_ID,
  },
  {
    start: FIRST_VACATION_START_ATTR_ID,
    end: FIRST_VACATION_END_ATTR_ID,
  },
  {
    start: SECOND_VACATION_START_ATTR_ID,
    end: SECOND_VACATION_END_ATTR_ID,
  },
  {
    start: THIRD_VACATION_START_ATTR_ID,
    end: THIRD_VACATION_END_ATTR_ID,
  },
  {
    start: FOURTH_VACATION_START_ATTR_ID,
    end: FOURTH_VACATION_END_ATTR_ID,
  },
  {
    start: FIFTH_VACATION_START_ATTR_ID,
    end: FIFTH_VACATION_END_ATTR_ID,
  },
];

const ACTOR_OPTIONS_PRIORITY: {
  [key: string]: number;
} = {
  'На мне': 1,
  'От меня': 2,
  'На моей команде': 3,
  Все: 4,
};

@injectable()
export class EventsMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
    private logger: Logger,
    private validator: ValidationService,
  ) {}

  processEmployeeInitials(event: EventListEntity | EventDetailEntity) {
    if (event.employee) {
      return `${event.employee.second_name} ${event.employee.first_name[0]}. ${event.employee.middle_name[0]}.`;
    }
    return '';
  }

  processEmployeeFullName(event: EventListEntity | EventDetailEntity) {
    if (event.employee) {
      return `${event.employee.second_name} ${event.employee.first_name} ${event.employee.middle_name}`;
    }
    return '';
  }

  processEmployeeShortName(employee: EventEmployee) {
    const firstInitial = `${employee.first_name[0]}.`;
    const middleInitial = employee.middle_name
      ? `${employee.middle_name[0]}.`
      : '';

    return `${employee.second_name} ${firstInitial}${middleInitial}`;
  }

  processEmployee(event: EventListEntity | EventDetailEntity) {
    if (event.employee) {
      const units: EventUnitData[] = [];

      if (event.employee.unit) {
        units.push({
          label: 'Управленческая',
          mobileLabel: 'Упр. структура',
          value: event.employee.unit,
          color: 'blue',
          width: '50%',
        });
      }

      if (event.employee.legal_unit) {
        units.push({
          label: 'Юридическая',
          mobileLabel: 'Юр. структура',
          value: event.employee.legal_unit,
          color: 'purple',
          width: '50%',
        });
      }

      if (units.length === 1) {
        units[0].width = '100%';
      }

      return {
        id: event.employee.id,
        name: `${event.employee.second_name} ${event.employee.first_name}`,
        fullName: this.processEmployeeFullName(event),
        shortName: this.processEmployeeShortName(event.employee),
        avatar: `${event.employee.second_name[0]}${event.employee.first_name[0]}`,
        personnelNumber: event.employee.personnel_number,
        birthday: event.employee.birthday,
        dismissed:
          !!event.employee.dismissed_at &&
          Date.now() > +new Date(event.employee.dismissed_at),
        dismissedAt: event.employee.dismissed_at
          ? this.format.toDate(event.employee.dismissed_at)
          : null,
        phone: event.employee.phone ?? '-',
        email: event.employee.email ?? '-',
        units: units,
        position: event.employee.position,
        periodStartDate: this.format.toDate(
          event.attributes.find(({ id }) => id === PERIOD_START_ID)
            ?.value as string,
        ),
        periodEndDate: this.format.toDate(
          event.attributes.find(({ id }) => id === PERIOD_END_ID)
            ?.value as string,
        ),
      };
    }
  }

  processStageName(stage: Action) {
    try {
      switch (stage.type) {
        case 'system_booking_approve':
        case 'system_booking_limits_exceeded_approve':
          return 'Согласование';
        case 'system_booking_hook':
        case 'system_linkedtrip_attrinfo':
        case 'system_linkedtrip_approveinfo':
        case 'system_booking_trip_create':
        case 'system_booking_trip_info':
        case 'system_booking_trip_change':
        case 'system_booking_trip_limit_approved':
        case 'system_create_request_sw':
        case 'system_item_voucher_sw':
        case 'system_cancel_request_sw':
        case 'system_business_trip_file_delivery':
          return 'Обработка';
        case 'system_booking_trip_ordering':
        case 'system_trip_info_sw':
        case 'system_booking_trip_limit':
          return 'Бронирование';
        default:
          return stage.name === 'Отказ от подписания' ? 'Отказано' : stage.name;
      }
    } catch (e) {
      this.logger.error(e);
    }
  }

  processStage(event: EventListEntity | EventDetailEntity) {
    try {
      if (!event.active_nodes || !event.active_nodes.length) {
        return;
      }

      const stage = event.active_nodes[0].action;

      let label = '';

      if (stage.type === 'upload') {
        label = 'Загрузить до';
      } else if (stage.type === 'accept') {
        label = 'Проверить до';
      } else if (
        stage.type === 'decline_sign' ||
        stage.type === 'declined_sign'
      ) {
        label = 'Отказано';
      } else if (
        stage.type === 'ukep_sign' ||
        stage.type === 'unep_sign' ||
        stage.type === 'ukep_sign_batch' ||
        stage.type === 'unep_sign_batch' ||
        stage.type === 'pep_sign' ||
        stage.type === 'pep_sign_batch'
      ) {
        label = 'Подписать до';
      } else if (stage.type === 'generate_document_from_template') {
        label = 'Создать до';
      }

      return {
        name: this.processStageName(stage),
        color: STAGE_COLORS[stage.type] || STAGE_COLORS.default,
        label,
      };
    } catch (e) {
      this.logger.error(e);
    }
  }

  processActiveStages(event: EventListEntity | EventDetailEntity) {
    if (!event.active_nodes || event.active_nodes.length === 0) {
      return;
    }

    return event.active_nodes.map(
      ({ action, responsible, custom_state, is_return }) => {
        const actionName =
          custom_state?.name === 'Отказ от подписания' ||
          action.name === 'Отказ от подписания'
            ? 'Отказано'
            : custom_state?.name || action.name;

        return {
          actionName,
          withReturn: is_return,
          responsible,
          description: custom_state?.description,
          ...(custom_state?.color ? { color: custom_state.color } : {}),
        };
      },
    );
  }

  processVacations(event: EventListEntity | EventDetailEntity) {
    const vacations: {
      start: Attribute;
      end: Attribute;
    }[] = [];

    vacationPeriods.forEach((period) => {
      const startAttr = event.attributes.find(
        (item) => item.id === period.start,
      );
      const endAttr = event.attributes.find((item) => item.id === period.end);

      if (startAttr && endAttr) {
        vacations.push({ start: startAttr, end: endAttr });
        return;
      }
    });

    return vacations.map(({ start, end }) => {
      const startDate = new Date(start.value as string);
      const endDate = new Date(end.value as string);
      const count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';

      return {
        start: this.format.toDate(start.value as string),
        end: this.format.toDate(end.value as string),
        count,
        isHolidaysInVacationInterval: Boolean(
          startDate <= endDate &&
          this.calendar.countOfHolidaysInInterval(startDate, endDate),
        ),
      };
    });
  }

  processBusinessTrip(event: EventListEntity | EventDetailEntity) {
    const start = event.attributes.find((attr) =>
      BUSINESS_TRIP_PERIODS.start.includes(attr.id),
    );
    const end = event.attributes.find((attr) =>
      BUSINESS_TRIP_PERIODS.end.includes(attr.id),
    );

    const startDate = start ? new Date(start.value as string) : null;
    const endDate = end ? new Date(end.value as string) : null;
    let count: string | number = '';

    if (startDate && endDate) {
      count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';
    }

    return {
      start: start ? this.format.toDate(start.value as string) : '',
      end: end ? this.format.toDate(end.value as string) : '',
      count,
    };
  }

  // Новые методы для обработки атрибутов (JS-385)
  processEmploymentPeriods(event: EventListEntity | EventDetailEntity) {
    const periods: Array<{
      start: string;
      end: string;
      count: string | number;
      label: string;
    }> = [];

    // 1. Период: Дата заключения договора - Дата окончания размещения
    const contractDate = event.attributes.find(
      (attr) => attr.id === CONTRACT_DATE_ATTR_ID,
    );
    const placementEndDate = event.attributes.find(
      (attr) => attr.id === PLACEMENT_END_DATE_ATTR_ID,
    );

    if (contractDate && placementEndDate) {
      const startDate = new Date(contractDate.value as string);
      const endDate = new Date(placementEndDate.value as string);
      const count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';

      periods.push({
        start: this.format.toDate(contractDate.value as string),
        end: this.format.toDate(placementEndDate.value as string),
        count,
        label: 'placement',
      });
    }

    // 2. Период: Дата заключения договора - Дата окончания испытательного срока
    if (contractDate && !placementEndDate) {
      const probationEndDate = event.attributes.find(
        (attr) => attr.id === PROBATION_END_DATE_ATTR_ID,
      );

      if (probationEndDate) {
        const startDate = new Date(contractDate.value as string);
        const endDate = new Date(probationEndDate.value as string);
        const count =
          startDate <= endDate
            ? this.calendar.countOfVacationDays(endDate, startDate)
            : 'Ошибка';

        periods.push({
          start: this.format.toDate(contractDate.value as string),
          end: this.format.toDate(probationEndDate.value as string),
          count,
          label: 'contract_probation',
        });
      }
    }

    // 3. Период: Дата начала испытательного срока - Дата окончания испытательного срока
    const probationStartDate = event.attributes.find(
      (attr) => attr.id === PROBATION_START_DATE_ATTR_ID,
    );
    const probationEndDate = event.attributes.find(
      (attr) => attr.id === PROBATION_END_DATE_ATTR_ID,
    );

    if (probationStartDate && probationEndDate && !contractDate) {
      const startDate = new Date(probationStartDate.value as string);
      const endDate = new Date(probationEndDate.value as string);
      const count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';

      periods.push({
        start: this.format.toDate(probationStartDate.value as string),
        end: this.format.toDate(probationEndDate.value as string),
        count,
        label: 'probation',
      });
    }

    return periods;
  }

  processSingleEmploymentDates(event: EventListEntity | EventDetailEntity) {
    const dates: string[] = [];

    // Дата увольнения
    const dismissalDate = event.attributes.find(
      (attr) => attr.id === DISMISSAL_DATE_ATTR_ID,
    );
    if (dismissalDate) {
      dates.push(this.format.toDate(dismissalDate.value as string));
    }

    // Дата заключения договора (если не использовалась в периодах)
    const contractDate = event.attributes.find(
      (attr) => attr.id === CONTRACT_DATE_ATTR_ID,
    );
    const placementEndDate = event.attributes.find(
      (attr) => attr.id === PLACEMENT_END_DATE_ATTR_ID,
    );
    const probationEndDate = event.attributes.find(
      (attr) => attr.id === PROBATION_END_DATE_ATTR_ID,
    );

    if (contractDate && !placementEndDate && !probationEndDate) {
      dates.push(this.format.toDate(contractDate.value as string));
    }

    return dates;
  }

  processCertificates(
    certificates: {
      id: string;
      name: string;
      user: string;
      company: string;
      activeFrom: string;
      activeTo: string;
    }[],
  ) {
    return certificates.map((certificate) => ({
      ...certificate,
      activeFrom: this.format.toDate(certificate.activeFrom),
      activeTo: this.format.toDate(certificate.activeTo),
    }));
  }

  /* eslint-disable no-magic-numbers */

  processCompanyListFilters(filters: Filter[], tags: string[] = []): Filter[] {
    if (!filters) {
      return [];
    }

    const filterByRequestNumberTooltip = t([
      ...tags.map(
        (tag) => `event.eventsList.filterByRequestNumberTooltip.${tag}`,
      ),
      'event.eventsList.filterByRequestNumberTooltip.default',
    ]);

    const availableFilters = [
      ...filters.filter((filter) => filter.type !== 'timerange'),
      ...filters.filter((filter) => filter.type === 'timerange'),
    ];

    return availableFilters
      .map((filter) => {
        if (filter.type === 'timerange') {
          return {
            ...filter,
            width: 8 as const,
            newLine: true,
            fullWidth: false,
          };
        }

        if (filter.type === 'multiple-search' && filter.key === 'unit_ids') {
          return {
            ...filter,
            width: 8 as const,
            fullWidth: false,
          };
        }

        if (filter.type === 'search' && filter.key === 'parent_event_id') {
          return {
            ...filter,
            width: 8 as const,
            disabled: true,
          };
        }

        if (
          filter.type === 'multiple-search' &&
          filter.key === 'employee_ids'
        ) {
          return {
            ...filter,
            width: 4 as const,
            twoLine: true,
            minInputValueLength: 3,
            fullWidth: false,
          };
        }

        if (filter.type === 'select' && filter.key === 'event_type_group') {
          return {
            ...filter,
            width: 4 as const,
            options: [...filter.options].sort((a, b) =>
              (a.label || '').toLowerCase() > (b.label || '').toLowerCase()
                ? 1
                : -1,
            ),
            autocomplete: true,
          };
        }

        if (filter.type === 'multiple-search' && filter.key === 'event_ids') {
          return {
            ...filter,
            tooltip: filterByRequestNumberTooltip,
          };
        }

        if (filter.type === 'multiple' && filter.key === 'status') {
          return {
            ...filter,
            width: 4 as const,
            clearable: true,
            fullWidth: false,
          };
        }

        if (filter.type === 'multiple' && filter.key === 'cancel_reason_type') {
          return {
            ...filter,
            width: 4 as const,
            clearable: true,
            fullWidth: false,
          };
        }

        if (filter.type === 'multiple' && filter.key === 'side') {
          return {
            ...filter,
            width: 4 as const,
            clearable: true,
            fullWidth: false,
          };
        }

        if (filter.type === 'bool' && filter.key === 'on_returned_node') {
          return {
            ...filter,
            width: 4 as const,
            fullWidth: false,
            labelPlacement: 'end' as const,
          };
        }

        if (filter.type === 'bool') {
          return {
            ...filter,
            width: 12 as const,
            fullWidth: false,
            labelPlacement: 'end' as const,
          };
        }

        if (filter.type === 'select' && filter.key === 'actor') {
          return {
            ...filter,
            options: [...filter.options].sort(
              (a, b) =>
                (ACTOR_OPTIONS_PRIORITY[a.label] || 99) -
                (ACTOR_OPTIONS_PRIORITY[b.label] || 99),
            ),
            width: 4 as const,
            fullWidth: false,
          };
        }

        return {
          ...filter,
          width: 4 as const,
          fullWidth: false,
        };
      })
      .map((filter, index) => {
        if (index === 1) {
          return {
            ...filter,
            newLine: true,
          };
        }

        return filter;
      });
  }

  /* eslint-disable no-magic-numbers */

  processListFilters(filters: Filter[], tags: string[] = []) {
    const filterByRequestNumberTooltip = t([
      ...tags.map(
        (tag) => `event.eventsList.filterByRequestNumberTooltip.${tag}`,
      ),
      'event.eventsList.filterByRequestNumberTooltip.default',
    ]);

    return filters.map((filter) => {
      if (filter.type === 'multiple-search' && filter.key === 'employee_ids') {
        return {
          ...filter,
          twoLine: true,
          minInputValueLength: 3,
          fullWidth: false,
        };
      }

      if (filter.type === 'search' && filter.key === 'parent_event_id') {
        return {
          ...filter,
          width: 12 as const,
          disabled: true,
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'unit_ids') {
        return {
          ...filter,
          fullWidth: false,
        };
      }

      if (filter.type === 'select' && filter.key === 'event_batch') {
        return {
          ...filter,
          autocomplete: true,
        };
      }

      if (filter.type === 'select' && filter.key === 'event_type_group') {
        return {
          ...filter,
          options: [...filter.options].sort((a, b) =>
            (a.label || '').toLowerCase() > (b.label || '').toLowerCase()
              ? 1
              : -1,
          ),
          autocomplete: true,
        };
      }

      if (filter.type === 'timerange') {
        return {
          ...filter,
          width: 8 as const,
          newLine: true,
          fullWidth: false,
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'event_ids') {
        return {
          ...filter,
          tooltip: filterByRequestNumberTooltip,
        };
      }

      if (filter.type === 'multiple' && filter.key === 'status') {
        return {
          ...filter,
          clearable: true,
          fullWidth: false,
        };
      }

      if (filter.type === 'multiple' && filter.key === 'cancel_reason_type') {
        return {
          ...filter,
          clearable: true,
          fullWidth: false,
        };
      }

      if (filter.type === 'multiple' && filter.key === 'side') {
        return {
          ...filter,
          clearable: true,
          fullWidth: false,
        };
      }

      return {
        ...filter,
        fullWidth: false,
      };
    });
  }

  processDeadline(deadline: string) {
    const durationDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(deadline), 1),
    );

    return `${this.format.toTime(durationDate)} ${this.format.toDate(
      durationDate,
    )}`;
  }

  processDeadlineAsSeparateFields(deadline: string) {
    const durationDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(deadline), 1),
    );

    return {
      date: this.format.toDate(durationDate),
      time: this.format.toTime(durationDate),
    };
  }

  async processFilters(eventsDto: GetEventsDto) {
    if (!eventsDto.filters?.preset_id) {
      await this.validator.validateOrReject(eventsDto);
    }

    const filters: AbsencesFilterType = {};

    for (const filter in eventsDto.filters) {
      const filterValue = eventsDto.filters[filter];
      if (filterValue === undefined || filterValue === null) {
        continue;
      }
      filters[filter] = filterValue;
    }

    if (eventsDto.tags) filters.tags = eventsDto.tags;
    if (eventsDto.companyId) {
      if (filters.company) {
        const filteredCompany = (filters.company as string[]).filter(
          (id) => id !== eventsDto.companyId,
        );
        filters.company = [...filteredCompany, eventsDto.companyId];
      } else {
        filters.company = [eventsDto.companyId];
      }
    }

    const { sortByIds } = eventsDto;
    const checkForFilterByEventIds = !!sortByIds?.length
      ? { event_ids: sortByIds }
      : {};

    const filterEventIds =
      sortByIds?.length || (filters.event_ids as string[] | undefined)?.length
        ? [
            ...(sortByIds || []),
            ...((filters.event_ids as string[] | undefined) || []),
          ]
        : undefined;

    return filters
      ? { ...filters, event_ids: filterEventIds }
      : checkForFilterByEventIds;
  }
}
